import React, { useState } from 'react';
import { Download, Filter, TrendingUp, Users, DollarSign, Eye } from 'lucide-react';
import { adminService } from '../services/adminService';
import { saveAs } from 'file-saver';

const Reports = () => {
  const [selectedReport, setSelectedReport] = useState('users');
  const [dateRange, setDateRange] = useState('30d');
  const [format, setFormat] = useState('csv');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const reports = [
    {
      id: 'users',
      title: 'User Analytics Report',
      description: 'Comprehensive user growth and engagement metrics',
      icon: Users,
      color: 'from-blue-500 to-blue-600',
    },
    {
      id: 'campaigns',
      title: 'Campaign Performance Report',
      description: 'Campaign effectiveness and ROI analysis',
      icon: TrendingUp,
      color: 'from-green-500 to-green-600',
    },
    {
      id: 'revenue',
      title: 'Revenue & Financial Report',
      description: 'Financial performance and transaction analysis',
      icon: DollarSign,
      color: 'from-purple-500 to-purple-600',
    },
    {
      id: 'applications',
      title: 'Application Analytics Report',
      description: 'Campaign application trends and success rates',
      icon: Eye,
      color: 'from-orange-500 to-orange-600',
    },
  ];

  const handleGenerateReport = async () => {
    setLoading(true);
    setReportData(null);
    try {
      const data = await adminService.generateReport(selectedReport, dateRange, format);
      setReportData(data);
    } catch (err) {
      setReportData({ error: err.message || 'Failed to generate report.' });
    }
    setLoading(false);
  };

  // Helper to get columns from report data
  const getColumns = () => {
    if (!reportData || !Array.isArray(reportData.data) || reportData.data.length === 0) return [];
    return Object.keys(reportData.data[0]);
  };

  // Helper to export CSV
  const handleExportCSV = () => {
    if (!reportData || !Array.isArray(reportData.data) || reportData.data.length === 0) return;
    const columns = getColumns();
    const csvRows = [
      columns.join(','), // header
      ...reportData.data.map(row =>
        columns.map(col => {
          let val = row[col];
          if (typeof val === 'object' && val !== null) val = JSON.stringify(val);
          return `"${String(val ?? '')}"`;
        }).join(',')
      ),
    ];
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `${reportData.reportType || 'report'}_${dateRange}.csv`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
          <p className="text-gray-600">Generate comprehensive reports and analytics</p>
        </div>
      </div>

      {/* Report Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {reports.map((report) => (
          <div
            key={report.id}
            onClick={() => setSelectedReport(report.id)}
            className={`card p-6 cursor-pointer transition-all hover:shadow-lg ${
              selectedReport === report.id ? 'ring-2 ring-primary-500' : ''
            }`}
          >
            <div className={`p-3 rounded-full bg-gradient-to-r ${report.color} w-fit mb-4`}>
              <report.icon className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{report.title}</h3>
            <p className="text-sm text-gray-600">{report.description}</p>
          </div>
        ))}
      </div>

      {/* Report Configuration */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Report Configuration</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="input-field w-full"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="1y">Last year</option>
              <option value="custom">Custom range</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Format</label>
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              className="input-field w-full"
            >
              <option value="pdf">PDF</option>
              <option value="excel">Excel</option>
              <option value="csv">CSV</option>
              <option value="json">JSON</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={handleGenerateReport}
              className="btn-primary flex items-center gap-2 w-full"
              disabled={loading}
            >
              <Download className="h-4 w-4" />
              {loading ? 'Generating...' : 'Generate Report'}
            </button>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-blue-600">12,847</p>
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Campaigns</p>
              <p className="text-2xl font-bold text-green-600">23</p>
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100">
              <DollarSign className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-purple-600">$45,231</p>
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-orange-100">
              <Eye className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Applications</p>
              <p className="text-2xl font-bold text-orange-600">156</p>
            </div>
          </div>
        </div>
      </div>

      {/* Report Preview - Table & Export */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center justify-between">
          Report Preview
          {!loading && reportData && Array.isArray(reportData.data) && reportData.data.length > 0 && (
            <button
              className="btn-secondary flex items-center gap-2"
              onClick={handleExportCSV}
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
          )}
        </h3>
        <div className="bg-gray-50 rounded-lg p-8 overflow-x-auto">
          {loading && <div className="text-center text-gray-500">Loading...</div>}
          {!loading && reportData && Array.isArray(reportData.data) && reportData.data.length > 0 && (
            <table className="table-auto w-full text-xs border">
              <thead>
                <tr>
                  {getColumns().map((col) => (
                    <th key={col} className="px-2 py-1 border-b font-semibold text-gray-700 bg-gray-100">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reportData.data.map((row, idx) => (
                  <tr key={idx}>
                    {getColumns().map((col) => (
                      <td key={col} className="px-2 py-1 border-b">
                        {typeof row[col] === 'object' && row[col] !== null
                          ? JSON.stringify(row[col])
                          : String(row[col] ?? '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!loading && reportData && (!Array.isArray(reportData.data) || reportData.data.length === 0) && (
            <div className="text-center text-gray-400">
              No data found for this report.
            </div>
          )}
          {!loading && !reportData && (
            <div className="text-center text-gray-400">
              Select a report type and click "Generate Report" to see data.
            </div>
          )}
        </div>
      </div>

      {/* Recent Reports */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Reports</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">User Analytics Report</h4>
                <p className="text-sm text-gray-500">Generated 2 hours ago</p>
              </div>
            </div>
            <button className="btn-secondary flex items-center gap-2">
              <Download className="h-4 w-4" />
              Download
            </button>
          </div>
          
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Campaign Performance Report</h4>
                <p className="text-sm text-gray-500">Generated 1 day ago</p>
              </div>
            </div>
            <button className="btn-secondary flex items-center gap-2">
              <Download className="h-4 w-4" />
              Download
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;
